import { Avatar, Box, Chip, Stack, Typography, alpha } from '@mui/material';
import {
  BadgeOutlined,
  CalendarTodayOutlined,
  EmailOutlined,
  PhoneIphoneOutlined,
} from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { getUser } from '../../../../helpers/api';
import { openNotification } from '../../../../helpers/notification';
import LoaderSkeleton from '../../../common/LoaderSkeleton';
import { Empty } from 'antd';
import { properCase } from '../../../../helpers/utils';

const BRAND = '#0c5bab';

const statusColors = {
  ACTIVE: '#2e7d32',
  PENDING: '#ed6c02',
  INACTIVE: '#d32f2f',
};

const getInitials = (name = '') =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');

export default function UserSettingDetail({ userId }) {
  const [userData, setUserData] = useState();
  const [userLoading, setUserLoading] = useState(false);

  useEffect(() => {
    if (userId) fetchUserData();
  }, [userId]);

  const fetchUserData = async () => {
    setUserLoading(true);
    try {
      const res = await getUser(userId);
      const { data, status } = res?.result;
      if (status?.code === '00') {
        setUserData(data);
      }
    } catch (error) {
      const errmsg = error?.data?.result?.status?.desc || 'Something went wrong. Please try again';
      openNotification({ type: 'error', message: errmsg });
    } finally {
      setUserLoading(false);
    }
  };

  const renderInfoRow = ({ icon, label, value }) => (
    <Stack key={label} direction="row" spacing={1.5} alignItems="center" sx={{ py: 1.25 }}>
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: '10px',
          display: 'grid',
          placeItems: 'center',
          bgcolor: alpha(BRAND, 0.08),
          color: BRAND,
          flexShrink: 0,
          '& svg': { fontSize: 18 },
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" display="block">
          {label}
        </Typography>
        <Typography variant="body2" fontWeight={600} noWrap title={value || ''}>
          {value || 'N/A'}
        </Typography>
      </Box>
    </Stack>
  );

  if (userLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <LoaderSkeleton />
        <LoaderSkeleton />
      </Box>
    );
  }

  if (!userData) {
    return (
      <Box sx={{ px: 2, py: 10 }}>
        <Empty description="Unable to load your profile" />
      </Box>
    );
  }

  const statusColor = statusColors[userData?.status] || '#757575';

  return (
    <Box>
      {/* Banner + avatar */}
      <Box
        sx={{
          height: 88,
          background: `linear-gradient(135deg, ${BRAND} 0%, #2f80d1 100%)`,
        }}
      />
      <Box sx={{ px: 3, mt: '-40px', textAlign: 'center' }}>
        <Avatar
          sx={{
            width: 80,
            height: 80,
            mx: 'auto',
            bgcolor: '#fff',
            color: BRAND,
            fontSize: 28,
            fontWeight: 700,
            border: '4px solid #fff',
            boxShadow: `0 6px 18px ${alpha(BRAND, 0.25)}`,
          }}
        >
          {getInitials(userData?.fullName)}
        </Avatar>
        <Typography variant="h6" fontWeight={700} sx={{ mt: 1.5, lineHeight: 1.3 }}>
          {userData?.fullName}
        </Typography>
        <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
          {userData?.userCategory && (
            <Chip
              label={properCase(userData.userCategory)}
              size="small"
              sx={{ fontWeight: 600, bgcolor: alpha(BRAND, 0.08), color: BRAND }}
            />
          )}
          {userData?.status && (
            <Chip
              label={properCase(userData.status)}
              size="small"
              icon={
                <Box
                  component="span"
                  sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: statusColor, ml: '8px !important' }}
                />
              }
              sx={{
                fontWeight: 600,
                bgcolor: '#fff',
                border: '1px solid',
                borderColor: 'divider',
                color: statusColor,
              }}
            />
          )}
        </Stack>
      </Box>

      {/* Details */}
      <Box sx={{ px: 3, pt: 2.5, pb: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
          Account details
        </Typography>
        <Box sx={{ '& > *:not(:last-child)': { borderBottom: '1px solid', borderColor: 'divider' } }}>
          {[
            { icon: <EmailOutlined />, label: 'Email address', value: userData?.email },
            { icon: <PhoneIphoneOutlined />, label: 'Phone number', value: userData?.phone },
            {
              icon: <BadgeOutlined />,
              label: 'User category',
              value: userData?.userCategory ? properCase(userData.userCategory) : '',
            },
            {
              icon: <CalendarTodayOutlined />,
              label: 'Member since',
              value: userData?.entryDate
                ? new Date(userData.entryDate).toLocaleDateString('en-NG', { dateStyle: 'medium' })
                : '',
            },
          ].map(renderInfoRow)}
        </Box>
      </Box>
    </Box>
  );
}
